// @ts-check
// apolo/src/lib/eventTypes.js — P5-T03 (software-to-9) + P2-T01 (code-tab-lift).
//
// The agent loop emits a stream of events via onEvent; they used to be untyped
// object literals, so a handler could silently miss a variant. They're modeled as a
// DISCRIMINATED UNION tagged by `type`, routed through an exhaustive switch with an
// assertNever default — under the scoped checkJs (tsconfig.contracts.json), ADDING a
// new event variant without handling it is a tsc error (the `never` no longer
// accepts it). Domain Modeling Made Functional — protocol shapes as tagged unions.
//
// P2-T01 — ADOPTED on the Code path: the union was expanded from 5 placeholder
// variants to the FULL set runLocalAgentLoopInner actually emits (grep'd from the
// loop's onEvent sites), so LoopEvent is now the honest protocol of the Code-tab
// loop, and agentLoop.js's onEvent param is typed against it (JSDoc import). Adding
// a new emit type → add a typedef to the union → summarizeEvent's switch fails
// exhaustiveness → `npm run tsc:fe-contract` is RED until it's handled.

// ── content / streaming ──
/** @typedef {{ type: 'assistant', content: string, reasoning?: string }} AssistantEvent */
/** @typedef {{ type: 'token', delta?: string }} TokenEvent */
/** @typedef {{ type: 'reasoning', delta?: string }} ReasoningEvent */
/** @typedef {{ type: 'done', content: string }} DoneEvent */

// ── tools ──
/** @typedef {{ type: 'tool_call', id: string, name: string, args: unknown, repaired?: boolean }} ToolCallEvent */
/** @typedef {{ type: 'tool_result', id: string, name: string, result: string, approved: boolean }} ToolResultEvent */
/** @typedef {{ type: 'auto_review', id: string, name: string, decision: string, reason?: string }} AutoReviewEvent */
/** @typedef {{ type: 'send_to_user', content: string }} SendToUserEvent */
/** @typedef {{ type: 'plan', plan: unknown }} PlanEvent */

// ── loop lifecycle / control ──
/** @typedef {{ type: 'loop_step', n: number, phase: string }} LoopStepEvent */
/** @typedef {{ type: 'loop_stop', stopReason: string, iterations: number, repeatInfo?: { tool: string, args: string, failing: boolean } }} LoopStopEvent */
/** @typedef {{ type: 'max_iterations', max: number }} MaxIterationsEvent */
/** @typedef {{ type: 'aborted' }} AbortedEvent */
/** @typedef {{ type: 'retry', attempt: number, delayMs: number, error: string }} RetryEvent */
/** @typedef {{ type: 'error', error: string }} ErrorEvent */

// ── telemetry / quality ──
/** @typedef {{ type: 'turn_trace', trace: unknown }} TurnTraceEvent */
/** @typedef {{ type: 'rag_quality', score?: number, used?: boolean }} RagQualityEvent */
/** @typedef {{ type: 'quality_audit', findings: unknown[], source?: string }} QualityAuditEvent */
/** @typedef {{ type: 'auto_correct', result: unknown }} AutoCorrectEvent */
/** @typedef {{ type: 'generative_fast', skipped: string }} GenerativeFastEvent */
/** @typedef {{ type: 'honest_progress_flag', flags: unknown[] }} HonestProgressFlagEvent */
/** @typedef {{ type: 'unverified_deliverable', files: string[] }} UnverifiedDeliverableEvent */

/**
 * The complete event protocol of the local agent loop (runLocalAgentLoopInner).
 * @typedef {AssistantEvent | TokenEvent | ReasoningEvent | DoneEvent
 *   | ToolCallEvent | ToolResultEvent | AutoReviewEvent | SendToUserEvent | PlanEvent
 *   | LoopStepEvent | LoopStopEvent | MaxIterationsEvent | AbortedEvent | RetryEvent | ErrorEvent
 *   | TurnTraceEvent | RagQualityEvent | QualityAuditEvent | AutoCorrectEvent
 *   | GenerativeFastEvent | HonestProgressFlagEvent | UnverifiedDeliverableEvent} LoopEvent
 */

/**
 * Exhaustiveness guard: reachable only if a LoopEvent variant is left unhandled. A
 * new variant added to the union makes `summarizeEvent`'s default pass a non-`never`
 * value here → tsc error. (Throws at runtime as a defensive backstop.)
 * @param {never} x
 * @returns {never}
 */
function assertNever(x) {
  throw new Error(`unhandled LoopEvent variant: ${JSON.stringify(x)}`);
}

/**
 * Summarize a loop event into a short, side-effect-free label. The switch is
 * EXHAUSTIVE — every LoopEvent variant has a case; the default proves it via
 * assertNever. This is the fitness function for the protocol: extend LoopEvent and
 * this stops compiling until the new variant is handled (the P2-T01 teeth).
 * @param {LoopEvent} ev
 * @returns {string}
 */
export function summarizeEvent(ev) {
  switch (ev.type) {
    case 'assistant': return `assistant(${ev.content.length})`;
    case 'token': return 'token';
    case 'reasoning': return 'reasoning';
    case 'done': return 'done';
    case 'tool_call': return `tool_call:${ev.name}`;
    case 'tool_result': return `tool_result:${ev.name}:${ev.approved ? 'ok' : 'denied'}`;
    case 'auto_review': return `auto_review:${ev.decision}`;
    case 'send_to_user': return 'send_to_user';
    case 'plan': return 'plan';
    case 'loop_step': return `loop_step:${ev.n}:${ev.phase}`;
    case 'loop_stop': return `loop_stop:${ev.stopReason}`;
    case 'max_iterations': return `max_iterations:${ev.max}`;
    case 'aborted': return 'aborted';
    case 'retry': return `retry:${ev.attempt}`;
    case 'error': return `error:${ev.error}`;
    case 'turn_trace': return 'turn_trace';
    case 'rag_quality': return 'rag_quality';
    case 'quality_audit': return `quality_audit:${ev.findings.length}`;
    case 'auto_correct': return 'auto_correct';
    case 'generative_fast': return `generative_fast:${ev.skipped}`;
    case 'honest_progress_flag': return `honest_progress_flag:${ev.flags.length}`;
    case 'unverified_deliverable': return `unverified_deliverable:${ev.files.length}`;
    default: return assertNever(ev);
  }
}

/**
 * T5 (fix/repeat-loop-overfire) — payload de telemetria do `agent_loop_stop`.
 * Extrai a ferramenta ofensora + o flag `failing` do repeatInfo pra que a taxa
 * de falso-positivo do repeat-guard (dependência quebrada vs loop genuíno) seja
 * MEDÍVEL no sink `observe`. Puro; campos ausentes viram null (não poluem as
 * outras paradas).
 * @param {{ stopReason?: string, iterations?: number, repeatInfo?: { tool?: string, failing?: boolean } }} evt
 */
export function loopStopObservation(evt = {}) {
  return {
    stopReason: evt.stopReason,
    iterations: evt.iterations,
    repeatTool: evt.repeatInfo?.tool ?? null,
    repeatFailing: evt.repeatInfo?.failing ?? null,
  };
}
